import type { Hint } from "@scoach/types";
import { Check, Copy, Doc, Image, Pin, Spark, ThumbDn, ThumbUp } from "@scoach/ui/icons";
import { type CSSProperties, useState } from "react";

import { api } from "../../../../lib/http.ts";
import { safeHref } from "../../../../lib/safeHref.ts";
import { useLiveMeetingStore } from "../store.ts";

export interface HintCardProps {
  hint: Hint;
  meetingId: string;
  style?: CSSProperties;
}

const COLOR_MAP = {
  blue: { tint: "var(--gc-blue-50)", strong: "var(--gc-blue)" },
  red: { tint: "var(--gc-red-50)", strong: "var(--gc-red)" },
  yellow: { tint: "var(--gc-yellow-50)", strong: "var(--gc-yellow)" },
  green: { tint: "var(--gc-green-50)", strong: "var(--gc-green)" },
  orange: { tint: "var(--gc-orange-50)", strong: "var(--gc-orange)" },
} as const;

const RTL_RE = /[֐-׿؀-ۿﭐ-﷿ﹰ-﻿]/;
function isRtl(text: string): boolean {
  return RTL_RE.test(text);
}

export function HintCard({ hint, meetingId, style }: HintCardProps) {
  const acted = useLiveMeetingStore((s) => s.actedHintIds.has(hint.id));
  const pinned = useLiveMeetingStore((s) => s.pinnedHintIds.has(hint.id));
  const markActed = useLiveMeetingStore((s) => s.markHintActed);
  const togglePinned = useLiveMeetingStore((s) => s.togglePinned);
  const activeTag = useLiveMeetingStore((s) => s.activeTagFilter);
  const setActiveTag = useLiveMeetingStore((s) => s.setActiveTagFilter);
  const [open, setOpen] = useState(true);

  const c = COLOR_MAP[hint.color];

  function handleAct() {
    markActed(hint.id);
    api(`/meetings/${meetingId}/hints/${hint.id}/feedback`, {
      method: "POST",
      body: { actedOn: true },
    }).catch(() => {});
  }
  function handleNotRelevant() {
    void meetingId;
  }

  function pinThis() {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const fn = (window as any).__sallyPinHint as ((id: string) => void) | undefined;
    if (fn) fn(hint.id);
    else togglePinned(hint.id);
  }

  return (
    <article
      className={`hint-card hint-in ${acted ? "is-acted" : ""} ${pinned ? "is-pinned" : ""} ${hint.priority === "high" ? "hint-priority-high" : ""}`}
      style={{ ...style, cursor: "pointer" }}
      onClick={pinThis}
      title={pinned ? "Click to unpin" : "Click to pin to top"}
    >
      <div className="hint-rail" style={{ background: c.strong }} />
      <div className="hint-body">
        {pinned && (
          <div className="hint-pinned-badge">
            <Pin size={12} /> Pinned to top
          </div>
        )}
        <header className="hint-head">
          <div className="hint-cat" style={{ background: c.tint, color: c.strong }}>
            <Spark size={12} /> {hint.category}
          </div>
          <div className="hint-meta mono">@ {hint.timestamp}</div>
          <div className="hint-confidence" title="Confidence">
            <span className="conf-bar">
              <span style={{ width: `${hint.confidence * 100}%`, background: c.strong }} />
            </span>
            <span className="conf-num mono">{Math.round(hint.confidence * 100)}%</span>
          </div>
          <button
            type="button"
            className={`icon-btn xs ${pinned ? "active" : ""}`}
            onClick={(e) => {
              e.stopPropagation();
              pinThis();
            }}
            title={pinned ? "Unpin" : "Pin to top"}
          >
            <Pin size={14} />
          </button>
          <button
            type="button"
            className="icon-btn xs"
            onClick={(e) => {
              e.stopPropagation();
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const fn = (window as any).__sallyHintInfographic as
                | ((id: string) => void)
                | undefined;
              fn?.(hint.id);
            }}
            title="Generate infographic"
          >
            <Image size={14} />
          </button>
        </header>

        <h3 className="hint-title" dir={isRtl(hint.title) ? "rtl" : undefined}>
          {hint.title}
        </h3>
        <p className="hint-summary" dir={isRtl(hint.summary) ? "rtl" : undefined}>
          {hint.summary}
        </p>

        {hint.comparisonTable && (
          <>
            <div
              className="hint-compare-topic"
              dir={isRtl(hint.comparisonTable.topic) ? "rtl" : undefined}
            >
              {hint.comparisonTable.topic}
            </div>
            <div className="hint-compare">
              <div
                className={`hint-compare-side is-${hint.comparisonTable.left.verdict ?? "neutral"}`}
              >
                <h4>{hint.comparisonTable.left.name}</h4>
                <ul>
                  {hint.comparisonTable.left.points.map((p, i) => (
                    <li key={`l-${i}`}>{p}</li>
                  ))}
                </ul>
              </div>
              <div
                className={`hint-compare-side is-${hint.comparisonTable.right.verdict ?? "neutral"}`}
              >
                <h4>{hint.comparisonTable.right.name}</h4>
                <ul>
                  {hint.comparisonTable.right.points.map((p, i) => (
                    <li key={`r-${i}`}>{p}</li>
                  ))}
                </ul>
              </div>
            </div>
            {hint.comparisonTable.recommendation && (
              <div
                className="hint-compare-rec"
                dir={isRtl(hint.comparisonTable.recommendation) ? "rtl" : undefined}
              >
                {hint.comparisonTable.recommendation}
              </div>
            )}
          </>
        )}

        {open && hint.proofPoints.length > 0 && (
          <ul className="hint-points">
            {hint.proofPoints.map((p, i) => (
              <li key={`${i}-${p.slice(0, 8)}`} dir={isRtl(p) ? "rtl" : undefined}>
                <span className="hp-dot" style={{ background: c.strong }} />
                {p}
              </li>
            ))}
          </ul>
        )}

        {hint.references && hint.references.length > 0 ? (
          <div className="hint-refs">
            <span className="hint-refs-label">Sources:</span>
            {hint.references.map((r) => (
              <a
                key={`${r.docId}-${r.page ?? ""}`}
                className="ref-chip"
                href={safeHref(r.url)}
                target="_blank"
                rel="noreferrer"
                onClick={(e) => e.stopPropagation()}
                title={`Open ${r.docName}${r.page ? ` at page ${r.page}` : ""}`}
              >
                <Doc size={12} /> {r.docName}
                {r.page ? ` · p.${r.page}` : ""}
              </a>
            ))}
          </div>
        ) : (
          <div className="hint-sources">
            {hint.sources.map((s) => (
              <span key={s} className="src-chip">
                <Doc size={12} /> {s}
              </span>
            ))}
          </div>
        )}

        {hint.tags && hint.tags.length > 0 && (
          <div
            className="hint-tags"
            style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}
          >
            {hint.tags.map((tag) => (
              <button
                key={tag}
                type="button"
                className={`tag-chip${activeTag === tag ? " on" : ""}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveTag(activeTag === tag ? null : tag);
                }}
                title={`Filter hints by "${tag}"`}
              >
                {tag}
              </button>
            ))}
          </div>
        )}

        <footer className="hint-foot">
          <div className="hint-foot-actions">
            <button
              type="button"
              className="ghost-btn"
              onClick={(e) => {
                e.stopPropagation();
                setOpen((o) => !o);
              }}
            >
              {open ? "Collapse" : "Expand"}
            </button>
            <button type="button" className="ghost-btn" onClick={(e) => e.stopPropagation()}>
              <Copy size={14} /> Copy
            </button>
            <button type="button" className="ghost-btn" onClick={(e) => e.stopPropagation()}>
              Send to Drive
            </button>
          </div>
          <div className="hint-foot-feedback">
            <button
              type="button"
              className={`icon-btn xs ${acted ? "active-good" : ""}`}
              onClick={(e) => {
                e.stopPropagation();
                handleAct();
              }}
              title="Used this"
            >
              <ThumbUp size={14} />
            </button>
            <button
              type="button"
              className="icon-btn xs"
              onClick={(e) => {
                e.stopPropagation();
                handleNotRelevant();
              }}
              title="Not relevant"
            >
              <ThumbDn size={14} />
            </button>
          </div>
        </footer>
        {acted && (
          <div className="acted-stripe">
            <Check size={12} /> Marked as used in conversation
          </div>
        )}
      </div>
    </article>
  );
}
